// Interpreter Licensure Service
// Handles state-specific interpreter license requirements and user license tracking

import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand, GetCommand, ScanCommand, DeleteCommand, UpdateCommand } from '@aws-sdk/lib-dynamodb';
import { fetchAuthSession } from 'aws-amplify/auth';
import { interpreterLicensureRequirements, getStateRequirements } from '../data/interpreterLicensureRequirements';

// Generate UUID for license IDs
const generateLicenseId = () => {
  return 'license_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Function to get DynamoDB client with Cognito credentials
const getDynamoClient = async () => {
  try {
    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials found. User may not be authenticated.');
    }

    const client = new DynamoDBClient({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });

    return DynamoDBDocumentClient.from(client);
  } catch (error) {
    throw error;
  }
};

// Environment-based table selection
const STATE_LICENSURE_TABLE = process.env.NODE_ENV === 'production' 
  ? 'state_interpreter_licensure' 
  : 'state_interpreter_licensure-dev';

export const interpreterLicensureService = {
  // Get all state requirements
  getAllStateRequirements: () => {
    return interpreterLicensureRequirements;
  },

  // Get requirements for a specific state
  getStateRequirements: (state) => {
    return getStateRequirements(state);
  },

  // Get user's state licenses from user profile
  getUserStateLicenses: async (userId) => {
    try {
      const docClient = await getDynamoClient();
      
      const usersTable = process.env.NODE_ENV === 'production' 
        ? 'asl-ceu-tracker-users' 
        : 'asl-ceu-tracker-users-dev';
      
      const command = new GetCommand({
        TableName: usersTable,
        Key: { userId }
      });

      const response = await docClient.send(command);
      
      if (response.Item && response.Item.stateLicenses) {
        // Convert DynamoDB format to our expected format
        // Ensure each license has a unique ID (migrate old array-based licenses)
        const licenses = response.Item.stateLicenses.map((license, index) => ({
          licenseId: license.licenseId || generateLicenseId(), // Use existing ID or generate new one
          userId: userId,
          state: license.state,
          licenseType: license.type || 'License',
          licenseNumber: license.number || '',
          issueDate: license.issueDate || '',
          expirationDate: license.expirationDate || '',
          auditDate: license.auditDate || '',
          status: 'active',
          addedDate: license.addedDate || new Date().toISOString()
        }));
        
        // If we had to generate new IDs, update the user profile
        const needsUpdate = response.Item.stateLicenses.some(license => !license.licenseId);
        if (needsUpdate) {
          console.log('🔄 Migrating state licenses to UUID-based IDs for user:', userId);
          const updatedLicenses = licenses.map(license => ({
            licenseId: license.licenseId,
            state: license.state,
            type: license.licenseType,
            number: license.licenseNumber,
            issueDate: license.issueDate,
            expirationDate: license.expirationDate,
            auditDate: license.auditDate,
            addedDate: license.addedDate
          }));
          
          const updateCommand = new UpdateCommand({
            TableName: usersTable,
            Key: { userId },
            UpdateExpression: 'SET stateLicenses = :licenses, updatedAt = :updatedAt',
            ExpressionAttributeValues: {
              ':licenses': updatedLicenses,
              ':updatedAt': new Date().toISOString()
            }
          });
          
          await docClient.send(updateCommand);
        }
        
        return { success: true, licenses };
      }
      
      return { success: true, licenses: [] };
    } catch (error) {
      console.error('Error getting user state licenses:', error);
      return { success: false, error: error.message, licenses: [] };
    }
  },

  // Add a state license for a user
  addUserStateLicense: async (userId, licenseData) => {
    try {
      const docClient = await getDynamoClient();
      
      const usersTable = process.env.NODE_ENV === 'production' 
        ? 'asl-ceu-tracker-users' 
        : 'asl-ceu-tracker-users-dev';
      
      // First get current user data
      const getCommand = new GetCommand({
        TableName: usersTable,
        Key: { userId }
      });
      
      const userResponse = await docClient.send(getCommand);
      if (!userResponse.Item) {
        return { success: false, error: 'User not found' };
      }
      
      const currentLicenses = userResponse.Item.stateLicenses || [];
      
      // Generate unique license ID
      const licenseId = generateLicenseId();
      
      // Add new license with UUID
      const newLicense = {
        licenseId: licenseId,
        state: licenseData.state,
        type: licenseData.licenseType || 'License',
        number: licenseData.licenseNumber || '',
        issueDate: licenseData.issueDate || '',
        expirationDate: licenseData.expirationDate || '',
        auditDate: licenseData.auditDate || '',
        addedDate: new Date().toISOString()
      };
      
      const updatedLicenses = [...currentLicenses, newLicense];
      
      // Update user profile
      const updateCommand = new UpdateCommand({
        TableName: usersTable,
        Key: { userId },
        UpdateExpression: 'SET stateLicenses = :licenses, updatedAt = :updatedAt',
        ExpressionAttributeValues: {
          ':licenses': updatedLicenses,
          ':updatedAt': new Date().toISOString()
        },
        ReturnValues: 'ALL_NEW'
      });
      
      await docClient.send(updateCommand);
      
      console.log('✅ Added new state license:', { userId, licenseId, state: licenseData.state });
      return { success: true, licenseId, license: newLicense };
    } catch (error) {
      console.error('❌ Error adding state license:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a state license
  updateUserStateLicense: async (licenseId, updates) => {
    try {
      const docClient = await getDynamoClient();
      
      // Extract userId from licenseId (format: license_timestamp_randomstring)
      // We need to find the user by searching through their licenses
      const usersTable = process.env.NODE_ENV === 'production' 
        ? 'asl-ceu-tracker-users' 
        : 'asl-ceu-tracker-users-dev';
      
      // Since we don't have userId directly, we need to find it
      // For now, we'll require userId to be passed in updates or derive it from context
      // This is a limitation we'll address by requiring userId in the API call
      
      if (!updates.userId) {
        return { success: false, error: 'User ID is required for license updates' };
      }
      
      const userId = updates.userId;
      
      // Get current user data
      const getCommand = new GetCommand({
        TableName: usersTable,
        Key: { userId }
      });
      
      const userResponse = await docClient.send(getCommand);
      if (!userResponse.Item || !userResponse.Item.stateLicenses) {
        return { success: false, error: 'User or licenses not found' };
      }
      
      const licenses = [...userResponse.Item.stateLicenses];
      const licenseIndex = licenses.findIndex(license => license.licenseId === licenseId);
      
      if (licenseIndex === -1) {
        return { success: false, error: 'License not found' };
      }
      
      // Update the specific license
      licenses[licenseIndex] = {
        ...licenses[licenseIndex],
        state: updates.state || licenses[licenseIndex].state,
        type: updates.licenseType || licenses[licenseIndex].type,
        number: updates.licenseNumber || licenses[licenseIndex].number,
        issueDate: updates.issueDate || licenses[licenseIndex].issueDate,
        expirationDate: updates.expirationDate || licenses[licenseIndex].expirationDate,
        auditDate: updates.auditDate || licenses[licenseIndex].auditDate,
        updatedAt: new Date().toISOString()
      };
      
      // Update user profile
      const updateCommand = new UpdateCommand({
        TableName: usersTable,
        Key: { userId },
        UpdateExpression: 'SET stateLicenses = :licenses, updatedAt = :updatedAt',
        ExpressionAttributeValues: {
          ':licenses': licenses,
          ':updatedAt': new Date().toISOString()
        }
      });
      
      await docClient.send(updateCommand);
      
      console.log('✅ Updated state license:', { userId, licenseId, state: updates.state });
      return { success: true, license: licenses[licenseIndex] };
    } catch (error) {
      console.error('❌ Error updating state license:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a state license
  deleteUserStateLicense: async (licenseId, userId) => {
    try {
      const docClient = await getDynamoClient();
      
      const usersTable = process.env.NODE_ENV === 'production' 
        ? 'asl-ceu-tracker-users' 
        : 'asl-ceu-tracker-users-dev';
      
      // Get current user data
      const getCommand = new GetCommand({
        TableName: usersTable,
        Key: { userId }
      });
      
      const userResponse = await docClient.send(getCommand);
      if (!userResponse.Item || !userResponse.Item.stateLicenses) {
        return { success: false, error: 'User or licenses not found' };
      }
      
      const licenses = [...userResponse.Item.stateLicenses];
      const licenseIndex = licenses.findIndex(license => license.licenseId === licenseId);
      
      if (licenseIndex === -1) {
        return { success: false, error: 'License not found' };
      }
      
      // Remove the license
      licenses.splice(licenseIndex, 1);
      
      // Update user profile
      const updateCommand = new UpdateCommand({
        TableName: usersTable,
        Key: { userId },
        UpdateExpression: 'SET stateLicenses = :licenses, updatedAt = :updatedAt',
        ExpressionAttributeValues: {
          ':licenses': licenses,
          ':updatedAt': new Date().toISOString()
        }
      });
      
      await docClient.send(updateCommand);
      
      console.log('✅ Deleted state license:', { userId, licenseId });
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting state license:', error);
      return { success: false, error: error.message };
    }
  },

  // Get CEU progress for state licenses
  calculateStateLicenseProgress: (userCEUs, stateLicense) => {
    // For now, assume state licenses follow RID requirements (8.0 CEUs)
    // This can be enhanced later with state-specific requirements
    const requiredCEUs = 8.0;
    
    // Filter CEUs that apply to this state license
    const applicableCEUs = userCEUs.filter(ceu => 
      ceu.isCurrentCycle && 
      parseFloat(ceu.ceuHours || 0) > 0
    );
    
    const totalCEUs = applicableCEUs.reduce((sum, ceu) => 
      sum + parseFloat(ceu.ceuHours || 0), 0
    );
    
    const progress = Math.min((totalCEUs / requiredCEUs) * 100, 100);
    
    return {
      totalCEUs: totalCEUs.toFixed(1),
      requiredCEUs: requiredCEUs.toFixed(1),
      progress: Math.round(progress),
      workshopCount: applicableCEUs.length,
      isComplete: totalCEUs >= requiredCEUs
    };
  }
};

export default interpreterLicensureService;